import { isAfter, isBefore, parseISO } from 'date-fns';
import { type ISO8601DateOnlyString, type Organisation } from 'types/models';
import { getCorrectOrganisationParent } from 'utils/modules/entities';

export interface OrganisationParentValidity {
   id?: number;
   validFrom?: string | null;
   validTo?: string | null;
}

export function isParentActive(parent: OrganisationParentValidity, from: string, to: string): boolean {
   return (
      (!parent.validFrom || !isAfter(parseISO(parent.validFrom), parseISO(to))) &&
      (!parent.validTo || !isBefore(parseISO(parent.validTo), parseISO(from)))
   );
}

// Strict full-period coverage: the parent link must span the entire selected period.
// Used by OrganisationTreePicker, which intentionally excludes mid-period links.
export function isOrganisationValidForPeriod(parents: OrganisationParentValidity[] | undefined, periodFrom: string, periodTo: string): boolean {
   if (!parents || parents.length === 0) return true;
   return parents.some((p) => isParentActive(p, periodFrom, periodTo));
}

// Strict full-period coverage: the parent link must span the entire selected period.
// Used by OrganisationTreePicker, which intentionally excludes mid-period links.
export function isOrganisationValidForPeriodAndParent(
   parents: OrganisationParentValidity[] | undefined,
   parentId: number | null | undefined,
   periodFrom: string,
   periodTo: string
): boolean {
   if (parentId == null) return true;
   if (!parents || parents.length === 0) return false;
   return parents.some(
      (p) => p.id === parentId && (p.validFrom == null || p.validFrom <= periodFrom) && (p.validTo == null || p.validTo >= periodTo)
   );
}

// Overlap semantics matching the Entities page (OrgaTree/OrgaChart/OrgaTable).
// Used by Closing Hub so grey-out behaviour stays consistent with the Entities views.
export function isOrganisationActiveForPeriod(
   parents: OrganisationParentValidity[] | undefined,
   parentId: number | null | undefined,
   periodFrom: ISO8601DateOnlyString,
   periodTo: ISO8601DateOnlyString
): boolean {
   if (parentId == null) return true;
   if (!parents || parents.length === 0) return false;
   const parentsWithValidation = getCorrectOrganisationParent({ parents } as Organisation, { from: periodFrom, to: periodTo });
   return parentsWithValidation.some((p) => (p as { id?: number }).id === parentId && !p.notValidForPeriod);
}
